import type { Character, Zone } from "@cloudnest/horror-race-engine";
import {
  ALL_CHARACTERS,
  ALL_ZONES,
  resolveAllZones,
} from "@cloudnest/horror-race-engine";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { CHARACTER_COLORS } from "./character-display";
import { HorrorCharacterIcon } from "./HorrorCharacterIcon";

const ZONE_PROGRESS_STEPS = 10;

type PendingByZone = Readonly<
  Record<Zone, Readonly<Partial<Record<Character, number>>>>
>;

interface ZoneBoardProps {
  readonly zones: Readonly<Record<Zone, readonly Character[]>>;
  readonly showResolution?: boolean;
  // T23-b placement interaction (active player only) ───────────────────────
  /** Drawer-only preview: temp coins per zone per character (not yet committed). */
  readonly pendingByZone?: PendingByZone;
  /** A coin is "picked up" → light valid drop zones. */
  readonly placementActive?: boolean;
  /** Zone the active coin already sits in — excluded from the drop highlight. */
  readonly activeCoinZone?: Zone | null;
  /** Drop the active coin into a zone (makes the column a tap target). */
  readonly onZoneSelect?: (zone: Zone) => void;
}

function countCoins(tokens: readonly Character[]): Record<Character, number> {
  const counts: Record<Character, number> = {
    VAMPIRE: 0,
    JIANGSHI: 0,
    GUMIHO: 0,
    MUMMY: 0,
    ZOMBIE: 0,
  };
  for (const char of tokens) {
    counts[char]++;
  }
  return counts;
}

function clampProgressSteps(count: number): number {
  return Math.min(Math.max(count, 0), ZONE_PROGRESS_STEPS);
}

interface ZoneColumnProps {
  readonly zone: Zone;
  readonly tokens: readonly Character[];
  readonly winner: Character | null;
  /**
   * 1등 후보 — the single token that would advance if this zone resolved now
   * (sole max; null on a tie). Shown as one color icon in the header. This is
   * the headline info; per-character counts are intentionally not displayed.
   */
  readonly candidate: Character | null;
  readonly showResolution: boolean;
  readonly pending: Readonly<Partial<Record<Character, number>>>;
  readonly droppable: boolean;
  readonly isActiveZone: boolean;
  readonly onSelect?: (zone: Zone) => void;
}

function ZoneColumn({
  zone,
  tokens,
  winner,
  candidate,
  showResolution,
  pending,
  droppable,
  isActiveZone,
  onSelect,
}: ZoneColumnProps) {
  const counts = countCoins(tokens);
  const total = tokens.length;
  const maxCount = Math.max(0, ...Object.values(counts));

  // Drop-target a11y applied via spread so the role/aria are only present when
  // the column is actually interactive (a picked-up coin can land here).
  const dropProps = droppable
    ? {
        role: "button" as const,
        tabIndex: 0,
        "aria-label": `+${zone} 존에 놓기`,
        onClick: () => onSelect?.(zone),
        onKeyDown: (e: ReactKeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect?.(zone);
          }
        },
      }
    : {};

  return (
    <div
      data-testid={`horror-zone-${zone}`}
      data-token-count={total}
      data-droppable={droppable}
      {...dropProps}
      className="flex flex-1 flex-col gap-1.5 rounded-lg px-2 py-2.5 transition-[box-shadow,border-color,opacity]"
      style={{
        backgroundColor: "var(--cn-horror-card-bg)",
        border: `1px solid ${
          droppable
            ? "var(--cn-horror-finished-flash)"
            : "var(--cn-horror-surface-border)"
        }`,
        boxShadow: droppable
          ? "0 0 0 1px var(--cn-horror-finished-flash), 0 0 10px color-mix(in srgb, var(--cn-horror-finished-flash) 45%, transparent)"
          : "none",
        cursor: droppable ? "pointer" : "default",
        opacity: isActiveZone ? 0.5 : 1,
      }}
    >
      {/* Header: +N label (left) · 1등 후보 marker (right). The marker is the
          single token that would move if the zone resolved now — the headline
          info, vs raw counts. While placing, the right slot guides the drop
          instead (여기에 놓기 / 현재 위치). min-h keeps all headers aligned. */}
      <div className="flex min-h-[1.25rem] items-center justify-between gap-1">
        <span
          data-testid="horror-zone-label"
          className="font-bold text-xl leading-none"
          style={{ color: "var(--cn-horror-zone-accent)" }}
        >
          +{zone}
        </span>
        {droppable ? (
          <span
            className="font-bold text-[10px]"
            style={{ color: "var(--cn-horror-finished-flash)" }}
          >
            여기에 놓기
          </span>
        ) : isActiveZone ? (
          <span
            className="text-[10px]"
            style={{ color: "var(--cn-horror-text-muted)" }}
          >
            현재 위치
          </span>
        ) : candidate ? (
          <span
            data-testid={`horror-zone-${zone}-candidate`}
            data-candidate={candidate}
            title="현재 1등 후보 — 정산 시 이동"
            className="inline-flex"
          >
            <HorrorCharacterIcon
              character={candidate}
              size={16}
              color={CHARACTER_COLORS[candidate]}
            />
          </span>
        ) : null}
      </div>

      {ALL_CHARACTERS.map((char) => {
        const base = counts[char];
        const delta = pending[char] ?? 0;
        const shown = base + delta;
        const isWinner = winner === char;
        const isLeader = base > 0 && base === maxCount;
        const baseSteps = clampProgressSteps(base);
        const totalSteps = clampProgressSteps(shown);
        const basePct = baseSteps * 10;
        const totalPct = totalSteps * 10;
        const deltaPct = Math.max(0, totalPct - basePct);
        const countColor =
          delta > 0
            ? "var(--cn-horror-finished-flash)"
            : shown > 0
              ? "var(--cn-horror-text)"
              : "var(--cn-horror-text-muted)";

        return (
          <div
            key={char}
            data-testid={`horror-zone-${zone}-${char}`}
            data-count={base}
            data-pending={delta}
            data-count-total={shown}
            data-base-steps={baseSteps}
            data-total-steps={totalSteps}
            data-is-leader={isLeader}
            data-is-winner={isWinner}
            className="flex items-center gap-1.5"
            style={{ opacity: shown === 0 ? 0.28 : 1 }}
          >
            <HorrorCharacterIcon
              character={char}
              size={15}
              color={CHARACTER_COLORS[char]}
            />
            <div
              className="relative flex h-[9px] flex-1 overflow-visible rounded-sm"
              style={{
                backgroundColor: "var(--cn-horror-track)",
                boxShadow:
                  "inset 0 0 0 1px color-mix(in srgb, var(--cn-horror-surface-border) 80%, transparent)",
              }}
            >
              {basePct > 0 && (
                <div
                  className="h-full rounded-sm transition-[width] duration-300 ease-out"
                  style={{
                    width: `${basePct}%`,
                    backgroundColor: CHARACTER_COLORS[char],
                    opacity: isWinner ? 1 : isLeader ? 0.75 : 0.55,
                  }}
                />
              )}
              {deltaPct > 0 && (
                // pending (temp, not committed) — dashed-gold segment appended to
                // the committed bar; together they fill to the previewed total.
                <div
                  className="h-full rounded-sm"
                  style={{
                    width: `${deltaPct}%`,
                    backgroundImage:
                      "repeating-linear-gradient(45deg, color-mix(in srgb, var(--cn-horror-finished-flash) 78%, transparent) 0 2px, color-mix(in srgb, var(--cn-horror-finished-flash) 24%, transparent) 2px 4px)",
                    outline: "1px dashed var(--cn-horror-finished-flash)",
                    outlineOffset: "-1px",
                  }}
                />
              )}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-sm"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to right, transparent 0, transparent calc(10% - 1px), color-mix(in srgb, var(--cn-horror-lane-divider) 70%, transparent) calc(10% - 1px), color-mix(in srgb, var(--cn-horror-lane-divider) 70%, transparent) 10%)",
                }}
              />
            </div>
            <span
              data-testid={`horror-zone-${zone}-${char}-count`}
              className="min-w-[1.125rem] text-right font-mono text-[10px] leading-none"
              style={{ color: countColor }}
            >
              {shown}
            </span>
          </div>
        );
      })}

      {showResolution && total > 0 && (
        <div
          data-testid={`horror-zone-${zone}-resolution`}
          data-winner={winner ?? ""}
          className="mt-0.5 pt-1.5 text-center text-[10px]"
          style={{
            borderTop: "1px solid var(--cn-horror-surface-border)",
            color: winner
              ? CHARACTER_COLORS[winner]
              : "var(--cn-horror-text-muted)",
            fontWeight: winner ? "bold" : "normal",
          }}
        >
          {winner ? (
            <span
              className="inline-flex items-center justify-center gap-1"
              style={{ color: CHARACTER_COLORS[winner] }}
            >
              <HorrorCharacterIcon
                character={winner}
                size={12}
                color={CHARACTER_COLORS[winner]}
              />
              +{zone}칸
            </span>
          ) : (
            <span style={{ color: "var(--cn-horror-text-muted)" }}>
              동점 — 이동없음
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const NO_PENDING: Readonly<Partial<Record<Character, number>>> = {};

export function ZoneBoard({
  zones,
  showResolution = false,
  pendingByZone,
  placementActive = false,
  activeCoinZone = null,
  onZoneSelect,
}: ZoneBoardProps) {
  // Sole-max token per zone (null on a tie) — the "1등 후보" that would advance
  // if the zone settled now. Computed from the COMMITTED zones (the placement
  // preview is separate), so it reflects the real "if settled now" outcome. The
  // header marker shows it live; the footer shows it as the winner on resolve.
  const resolutions = resolveAllZones(zones);
  const candidateByZone: Record<Zone, Character | null> = {
    1: resolutions.find((r) => r.zone === 1)?.winner ?? null,
    2: resolutions.find((r) => r.zone === 2)?.winner ?? null,
    4: resolutions.find((r) => r.zone === 4)?.winner ?? null,
  };

  return (
    <div className="flex gap-2">
      {ALL_ZONES.map((zone) => (
        <ZoneColumn
          key={zone}
          zone={zone}
          tokens={zones[zone]}
          candidate={candidateByZone[zone]}
          winner={showResolution ? candidateByZone[zone] : null}
          showResolution={showResolution}
          pending={pendingByZone?.[zone] ?? NO_PENDING}
          droppable={placementActive && zone !== activeCoinZone}
          isActiveZone={placementActive && zone === activeCoinZone}
          onSelect={onZoneSelect}
        />
      ))}
    </div>
  );
}
